/* 
Copyright© Celligence International, LLC - 2024
This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { Fragment, memo, useState } from 'react';
import Style from './../../css/LoanHistory.module.css';
import { Table, TableHead, TableRow, TableBody, TableCell, Checkbox, Button } from '@mui/material';
import DefaultModal from './DefaultModal';
import type { SelectType } from '../../types/SelectType';
import type { SelectListPropsType } from '../../types/SelectListPropsType';

const SelectListModal = memo((props: SelectListPropsType) => {
  const [allSelected, setAllSelected] = useState(true);
  const [someSelected, setSomeSelected] = useState(false);

  const handleChange = (index: number) => {
    props.updateRowChecked(index);
    const checked = props.rows.filter((item) => item.selected === true);

    if (props.rows.length === checked.length) {
      //all are selected
      setAllSelected(true);
      setSomeSelected(false);
    } else if (checked.length === 0) {
      //none are selected
      setAllSelected(false);
      setSomeSelected(false);
    } else {
      //some are selected
      setAllSelected(false);
      setSomeSelected(true);
    }
  };

  const handleChangeAll = () => {
    if (allSelected) {
      //all are selected - now select none
      props.checkAllRows(false);
      setAllSelected(false);
      setSomeSelected(false);
    } else if (someSelected) {
      //some are selected - now select all
      props.checkAllRows(true);
      setAllSelected(true);
      setSomeSelected(false);
    } else {
      //none are selected - now select all
      props.checkAllRows(true);
      setAllSelected(true);
      setSomeSelected(false);
    }
  };

  return (
    <Fragment>
      <DefaultModal
        open={props.open}
        onClose={props.handleCancel}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
        title={<h1>{props.title}</h1>}
        content={
          <div className={Style.height}>
            <Table stickyHeader aria-label='sticky table'>
              <TableHead>
                <TableRow>
                  <TableCell align='center' className={Style.headerCell}>
                    <Checkbox checked={allSelected} indeterminate={someSelected} onClick={handleChangeAll} />
                  </TableCell>
                  <TableCell align='center' className={Style.headerCell}>
                    {props.columnTitle}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {props.rows.map((row: SelectType, index: number) => (
                  <TableRow key={index}>
                    <TableCell align='center' className={Style.cell}>
                      <Checkbox checked={row.selected} onClick={() => handleChange(index)} />
                    </TableCell>
                    <TableCell align='center' className={Style.cell}>
                      {row.value}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        }
        footer={
          <div className={Style.footer}>
            <Button variant='outlined' onClick={props.handleCancel} className={Style.cancelButton}>
              Cancel
            </Button>
            <Button variant='contained' className={Style.button} onClick={props.handleSubmit}>
              Submit
            </Button>
          </div>
        }
      />
    </Fragment>
  );
});

export default SelectListModal;
